import React, { useState, useEffect, useMemo } from 'react';
import { sanitizeString } from '../utils/security';
import { fetchCategories } from '../services/categoriesService';
import { fetchAllContentsByPaginating } from '../services/productsService';
import {
  foldedSearchTokens,
  productMatchesFoldedTokens,
  buildProductCategoryTree,
  annotateProductCategoryAcc,
} from '../utils/productPickerCore';
import { FoldedSearchHighlight } from './FoldedSearchHighlight';

const safeText = (value) => sanitizeString(value ?? '');

/**
 * Müşteri cihaz ekleme vb. için: Servis kaydı "Ürün Seç" ile aynı mantık (kategori ağacı + yerel filtre + sarı vurgu).
 */
export default function DeviceProductTreePicker({
  selectedId,
  onSelect,
  disabled = false,
}) {
  const [urunler, setUrunler] = useState([]);
  const [kategoriler, setKategoriler] = useState([]);
  const [loading, setLoading] = useState(true);
  const [arama, setArama] = useState('');
  const [aramaDebounced, setAramaDebounced] = useState('');
  const [accordionOpenIds, setAccordionOpenIds] = useState({});

  useEffect(() => {
    const t = setTimeout(() => {
      setAramaDebounced(arama.trim().slice(0, 100));
    }, 300);
    return () => clearTimeout(t);
  }, [arama]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      fetchCategories({ kategoriTipi: 'urun' }),
      fetchAllContentsByPaginating({ icerikTipi: 'urun', aktif: true }),
    ])
      .then(([catRes, urunList]) => {
        if (cancelled) return;
        const cats = catRes?.veri?.kategoriler || [];
        setKategoriler(Array.isArray(cats) ? cats : []);
        setUrunler(Array.isArray(urunList) ? urunList : []);
      })
      .catch(() => {
        if (!cancelled) {
          setKategoriler([]);
          setUrunler([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const kategoriYoluMap = useMemo(() => {
    const cats = kategoriler;
    const byId = new Map(cats.map((c) => [String(c.id || c._id), c]));
    const pathFor = (kid) => {
      if (!kid) return 'Kategorisiz';
      const parts = [];
      let cur = byId.get(String(kid));
      let guard = 0;
      while (cur && guard < 64) {
        guard += 1;
        parts.unshift(safeText(cur.kategoriAdi));
        const pid = cur.ustKategoriId;
        if (!pid) break;
        cur = byId.get(String(pid));
      }
      return parts.length ? parts.join(' › ').trim() : 'Kategorisiz';
    };
    return { byId, pathFor };
  }, [kategoriler]);

  const urunlerFiltreli = useMemo(() => {
    const tokens = foldedSearchTokens(aramaDebounced);
    let list = urunler;
    if (tokens.length) {
      list = list.filter((u) => productMatchesFoldedTokens(u, tokens));
    }
    list = [...list];
    list.sort((a, b) => {
      const pa = kategoriYoluMap.pathFor(a.kategoriId);
      const pb = kategoriYoluMap.pathFor(b.kategoriId);
      const c = pa.localeCompare(pb, 'tr', { sensitivity: 'base' });
      if (c !== 0) return c;
      const na = String(a.icerikAdi || a.urunAdi || '');
      const nb = String(b.icerikAdi || b.urunAdi || '');
      return na.localeCompare(nb, 'tr', { sensitivity: 'base' });
    });
    return list;
  }, [urunler, aramaDebounced, kategoriYoluMap]);

  const accTree = useMemo(() => {
    const rawRoots = buildProductCategoryTree(kategoriler);
    const pmap = new Map();
    urunlerFiltreli.forEach((u) => {
      const kid = u.kategoriId != null ? String(u.kategoriId) : '';
      if (!pmap.has(kid)) pmap.set(kid, []);
      pmap.get(kid).push(u);
    });
    return rawRoots.map((n) => annotateProductCategoryAcc(n, pmap));
  }, [kategoriler, urunlerFiltreli]);

  const urunlerKategorisiz = useMemo(
    () => urunlerFiltreli.filter((u) => !u.kategoriId),
    [urunlerFiltreli]
  );

  useEffect(() => {
    if (kategoriler.length === 0) return;
    const roots = buildProductCategoryTree(kategoriler);
    setAccordionOpenIds((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      const o = {};
      roots.forEach((r) => {
        o[r.cid] = true;
      });
      return o;
    });
  }, [kategoriler]);

  useEffect(() => {
    if (!aramaDebounced.trim()) return;
    const byId = new Map(kategoriler.map((c) => [String(c.id || c._id), c]));
    setAccordionOpenIds((prev) => {
      const next = { ...prev };
      const pullUp = (kid) => {
        let cur = kid ? byId.get(String(kid)) : null;
        while (cur) {
          next[String(cur.id || cur._id)] = true;
          const p = cur.ustKategoriId;
          cur = p ? byId.get(String(p)) : null;
        }
      };
      urunlerFiltreli.forEach((u) => {
        if (u.kategoriId) pullUp(u.kategoriId);
      });
      return next;
    });
  }, [aramaDebounced, urunlerFiltreli, kategoriler]);

  const toggleAccordion = (cid) => {
    setAccordionOpenIds((p) => ({ ...p, [cid]: !p[cid] }));
  };

  const renderAccNode = (item, depth = 0) => {
    if (!item.visible) return null;
    const open = accordionOpenIds[item.cid] === true;
    const visibleChildren = item.children.filter((c) => c.visible);
    const hasBody = item.products.length > 0 || visibleChildren.length > 0;

    return (
      <div key={item.cid} style={{ marginBottom: 8 }}>
        <button
          type="button"
          onClick={() => hasBody && toggleAccordion(item.cid)}
          disabled={disabled || !hasBody}
          style={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 12px',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            background: open ? '#f1f5f9' : '#ffffff',
            cursor: hasBody && !disabled ? 'pointer' : 'default',
            textAlign: 'left',
            fontWeight: 600,
            fontSize: '14px',
            color: '#1f2937',
            opacity: hasBody ? 1 : 0.85,
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
          }}
        >
          <span
            style={{
              width: 18,
              display: 'inline-flex',
              justifyContent: 'center',
              color: '#64748b',
              fontSize: '12px',
            }}
            aria-hidden
          >
            {hasBody ? <i className={`fas fa-chevron-${open ? 'down' : 'right'}`} /> : <span style={{ width: 12 }} />}
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>{safeText(item.kategoriAdi || '—')}</span>
          {item.products.length > 0 && (
            <span style={{ fontSize: '12px', fontWeight: 500, color: '#64748b', flexShrink: 0 }}>
              {item.products.length} ürün
            </span>
          )}
        </button>
        {open && hasBody && (
          <div
            style={{
              marginTop: 8,
              paddingLeft: depth > 0 ? 14 : 4,
              borderLeft: depth > 0 ? '2px solid #e2e8f0' : 'none',
            }}
          >
            {visibleChildren.map((ch) => renderAccNode(ch, depth + 1))}
            {item.products.map((u) => {
              const uid = u.id || u._id;
              const sel = String(selectedId || '') === String(uid);
              const ad = u.icerikAdi || u.urunAdi || '—';
              const kod = u.icerikKodu || u.urunKodu || '';
              return (
                <button
                  key={uid}
                  type="button"
                  disabled={disabled}
                  onClick={() => onSelect(u)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '12px 14px',
                    marginBottom: 8,
                    borderRadius: '8px',
                    border: sel ? '2px solid #2563eb' : '1px solid #e5e7eb',
                    background: sel ? '#eff6ff' : '#fff',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    transition: 'border-color 0.15s ease, background 0.15s ease',
                  }}
                >
                  <div style={{ fontWeight: sel ? 600 : 500, color: '#1f2937', lineHeight: 1.4 }}>
                    <FoldedSearchHighlight text={ad} queryRaw={aramaDebounced} />
                  </div>
                  {kod ? (
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6b7280',
                        marginTop: 4,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      }}
                    >
                      <FoldedSearchHighlight text={kod} queryRaw={aramaDebounced} />
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280' }}>
        <i className="fas fa-spinner fa-spin" style={{ marginRight: '8px' }} />
        Ürün ve kategoriler yükleniyor...
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <div>
        <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 500, color: '#4b5563' }}>
          <i className="fas fa-search" style={{ marginRight: '6px', color: '#9ca3af' }} />
          Ürün adı veya kod ile ara
        </label>
        <input
          type="text"
          value={arama}
          onChange={(e) => setArama(e.target.value)}
          placeholder="Kelime yazın; ağaçta eşleşenler vurgulanır"
          maxLength={100}
          disabled={disabled}
          style={{
            width: '100%',
            padding: '12px 14px',
            borderRadius: '8px',
            border: '1px solid #d1d5db',
            fontSize: '14px',
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px',
        }}
      >
        <span style={{ fontSize: '13px', fontWeight: 500, color: '#4b5563' }}>
          <i className="fas fa-folder-tree" style={{ marginRight: '6px', color: '#9ca3af' }} />
          Kategoriler ve ürünler
        </span>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            type="button"
            onClick={() => {
              const all = {};
              const walkOpen = (nodes) => {
                nodes.forEach((n) => {
                  if (!n.visible) return;
                  const has = n.products.length > 0 || n.children.some((c) => c.visible);
                  if (has) all[n.cid] = true;
                  walkOpen(n.children);
                });
              };
              walkOpen(accTree);
              setAccordionOpenIds(all);
            }}
            style={{
              fontSize: '12px',
              padding: '6px 10px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              background: '#fff',
              color: '#475569',
              cursor: 'pointer',
            }}
          >
            Tümünü aç
          </button>
          <button
            type="button"
            onClick={() => setAccordionOpenIds({})}
            style={{
              fontSize: '12px',
              padding: '6px 10px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              background: '#fff',
              color: '#475569',
              cursor: 'pointer',
            }}
          >
            Tümünü kapat
          </button>
        </div>
      </div>
      <div
        style={{
          maxHeight: 'min(50vh, 420px)',
          overflowY: 'auto',
          padding: '4px',
          borderRadius: '10px',
          border: '1px solid #e5e7eb',
          background: '#fafafa',
        }}
      >
        {accTree.filter((n) => n.visible).map((n) => renderAccNode(n, 0))}
        {urunlerKategorisiz.length > 0 && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #e5e7eb' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: 8, paddingLeft: 4 }}>
              Kategorisiz ürünler
            </div>
            {urunlerKategorisiz.map((u) => {
              const uid = u.id || u._id;
              const sel = String(selectedId || '') === String(uid);
              const ad = u.icerikAdi || u.urunAdi || '—';
              const kod = u.icerikKodu || u.urunKodu || '';
              return (
                <button
                  key={uid}
                  type="button"
                  disabled={disabled}
                  onClick={() => onSelect(u)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '12px 14px',
                    marginBottom: 8,
                    borderRadius: '8px',
                    border: sel ? '2px solid #2563eb' : '1px solid #e5e7eb',
                    background: sel ? '#eff6ff' : '#fff',
                    cursor: disabled ? 'not-allowed' : 'pointer',
                  }}
                >
                  <div style={{ fontWeight: sel ? 600 : 500, color: '#1f2937' }}>
                    <FoldedSearchHighlight text={ad} queryRaw={aramaDebounced} />
                  </div>
                  {kod ? (
                    <div
                      style={{
                        fontSize: '12px',
                        color: '#6b7280',
                        marginTop: 4,
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      }}
                    >
                      <FoldedSearchHighlight text={kod} queryRaw={aramaDebounced} />
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}
        {urunlerFiltreli.length === 0 && (
          <div style={{ padding: '24px', textAlign: 'center', color: '#64748b', fontSize: '14px' }}>
            Aramanızla eşleşen ürün yok.
          </div>
        )}
      </div>
    </div>
  );
}
